import React, { useState, useEffect, useRef } from 'react';
import './card.css';
import DescCard from './descCard';
import UpdateProduct from './UpdateProduct';
import TrashBinModal from './TrashBinModal';
import ProductInfo from '../dashboard/ProductInfo';
import { userLimits } from '../dashboard/uitls/userLimits';
import { imageOnError } from '../../services/method/methods';

const DebouncedLazyImage = ({ src, alt, quality, referTo, onError = imageOnError, style }) => {
  const [isVisible, setIsVisible] = useState(false);
  const imageRef = useRef(null);
  const observerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  useEffect(() => {
    const createObserver = () => {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Clear any existing timeout
              if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
              }

              // Set a new timeout to load the image
              scrollTimeoutRef.current = setTimeout(() => {
                setIsVisible(true);
                // Disconnect observer once image is loaded
                observerRef.current.unobserve(entry.target);
              }, 2000);
            } else {
              // If element is not intersecting, clear the timeout
              if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
              }
            }
          });
        },
        {
          root: null, // viewport
          rootMargin: '0px',
          threshold: 0.1, // trigger when 10% of image is visible
        }
      );

      if (imageRef.current) {
        observerRef.current.observe(imageRef.current);
      }
    };

    createObserver();

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [src]);

  const placeholderSrc =
    'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

  return (
    <img
      ref={imageRef}
      onError={onError}
      src={
        isVisible
          ? `${process.env.REACT_APP_SERVER_API}/pictures/${src}/${referTo}?quality=${
              quality || 100
            }`
          : placeholderSrc
      }
      alt={alt}
      style={{
        maxWidth: '95%',
        maxHeight: '95%',
        opacity: isVisible ? 1 : 0.5,
        filter: isVisible ? 'none' : 'blur(5px)',
        transition: 'opacity 0.5s, filter 0.5s',
        ...style,
      }}
    />
  );
};

function ProductCard(props) {
  const { productsDash } = props;
  let quality = props.quality ? props.quality : 100;
  const [show, setShow] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [updateShow, setUpdateShow] = useState(false);
  const [informationShow, setInformationShow] = useState(false);

  const { superAdmin, access } = userLimits('', '', 'Products', 'edit_prod');

  const backgroundStyle = {
    backgroundColor: props.prodType == 'prod' ? '#3f51b5' : '#ffc107',
    color: 'white',
  };

  const cursorStyle = {
    cursor: 'pointer',
  };

  const style =
    window.location.pathname.includes('/dashboard') ||
    window.location.pathname.includes('/brand') ||
    window.location.pathname.includes('/viewmymachines') ||
    window.location.pathname.includes('/viewmyusers');

  //calculate price
  let discountedPrice = 0;
  if (props.discount !== 0) {
    discountedPrice =
      props.percentage === true
        ? props.priceD * (1 - props.discount)
        : props.priceD - props.discount;
  }

  if (style) {
    return (
      <div
        id={`product-${props.id}`}
        className="col-xs-6 col-sm-6 col-md-6 col-lg-4 mb-5 cardBorder"
      >
        {access && (
          <div>
            <button
              onClick={(e) => {
                setShow(true);
                e.stopPropagation();
              }}
              className="delete_btn"
            >
              <span className="iconify" data-icon="mdi-trash-can" data-inline="false"></span>
            </button>
          </div>
        )}
        <UpdateProduct
          productsDash={productsDash}
          access={access}
          id={props.id}
          name={props.name}
          upc={props.upc}
          description={props.description}
          category={props.category}
          price={props.price}
          video={props.video}
          brand={props.brand}
          quantity={props.quantity}
          referTo={props.referTo}
          show={updateShow}
          onHide={() => setUpdateShow(false)}
        />
        <TrashBinModal
          id={props.id}
          name={props.name}
          show={show}
          onHide={() => {
            setShow(false);
          }}
        />
        <div style={cursorStyle} onClick={() => setInformationShow(true)} className="cardBorder">
          <div style={{ width: '100%', height: '200px' }}>
            <DebouncedLazyImage
              src={props.image[0]}
              alt="product"
              quality={quality}
              referTo={props.referTo}
            />
          </div>
          <div className="card-body prodbody" style={backgroundStyle}>
            <button
              onClick={(e) => {
                setUpdateShow(true);
                e.stopPropagation();
              }}
              className="edit_btn"
            >
              <span className="iconify" data-icon="akar-icons:edit" data-inline="false"></span>
            </button>
            <h5 className="card-title d-flex flex-column justify-content-center">
              <div className="showh5">{props.name.en}</div>
            </h5>
            <h5 className="card-text d-flex justify-content-center">{props.price}</h5>
            {superAdmin && (
              <h6>
                Refer to{' '}
                <span
                  style={{
                    color: 'yellow',
                  }}
                >
                  {props.referTo}
                </span>
              </h6>
            )}
          </div>
        </div>
        <ProductInfo
          id={props.id}
          name={props.name}
          description={props.description}
          category={props.category}
          price={props.price}
          image={props.image}
          brand={props.brand}
          video={props.video}
          quantity={props.quantity}
          referTo={props.referTo}
          show={informationShow}
          onHide={() => setInformationShow(false)}
        />
      </div>
    );
  } else {
    return (
      <div
        id={`product-${props.id}`}
        className="col-xs-6 col-sm-6 col-md-6 col-lg-4 mb-5 cardBorder"
      >
        <button onClick={() => setModalShow(true)}>
          <div className="cardBorder">
            <div style={{ width: '100%', height: '200px' }}>
              <DebouncedLazyImage
                src={props.image[0]}
                alt="product"
                quality={quality}
                referTo={props.referTo}
              />
            </div>
            <div className="card-body prodbody">
              <h5 className="card-title d-flex justify-content-center">{props.name}</h5>
              {!props.discount ? (
                <h5 className="card-text cardtext d-flex justify-content-center">{props.price}</h5>
              ) : (
                <h5
                  className="card-text cardtext d-flex justify-content-center"
                  style={{ textDecoration: 'line-through' }}
                >
                  {props.price}
                </h5>
              )}
              {discountedPrice !== 0 && (
                <h5 className="card-text cardtext d-flex justify-content-center">
                  {'$ ' + discountedPrice}
                </h5>
              )}
              {props.buyX && (
                <h5 className="card-text cardtext d-flex justify-content-center">
                  {'Buy ' + props.buyX + ' Get'}
                </h5>
              )}
              {props.discount !== 0 && props.percentage === true && (
                <h5 className="card-text cardtext d-flex justify-content-center">
                  {props.discount * 100 + '% off!'}
                </h5>
              )}
              {props.discount !== 0 && props.percentage === false && (
                <h5 className="card-text cardtext d-flex justify-content-center">
                  {'$' + props.discount + ' off!'}
                </h5>
              )}
            </div>
          </div>
        </button>
        <DescCard
          show={modalShow}
          onHide={() => setModalShow(false)}
          stock={props.stock}
          id={props.id}
          key={props.id}
          description={props.description}
          name={props.name}
          price={props.price}
          priceD={props.priceD}
          discount={props.discount}
          image={props.image}
          category={props.category}
          add={props.add}
          alterquantity={props.alterquantity}
          cart={props.cart}
          percentage={props.percentage}
          discountedPrice={discountedPrice}
          buyX={props.buyX}
        />
      </div>
    );
  }
}

export default ProductCard;
